import { useState } from "react";

import { Box } from "@mui/material";

import { ConversationProvider } from "@/hooks/ConversationProvider";
import { LunaTabName, UserRole } from "@/types";

import ChatPane from "./ChatPane";
import RealtimeSnackbar from "./Luna/features/RealtimeSnackbar/RealtimeSnackbar";
import LunaStudent from "./Luna/student";
import LunaTutor from "./Luna/tutor";
import LessonNotes from "./Notes/LessonNotes";
import ReadOnlyChatPane from "./ReadOnlyChatPane";
import SidebarTabs from "./SidebarTabs";

export enum ActiveTab {
  Chat = 0,
  Notes = 1,
  Luna = 2,
}

export enum SidebarState {
  STANDARD = "STANDARD",
  EXPANDED = "EXPANDED",
  COLLAPSED = "COLLAPSED",
}

interface SidebarProps {
  className?: string;
  userRole: UserRole;
  inRecordedLessonView?: boolean;
  lessonId?: string;
  lessonType: string;
  sidebarState: SidebarState;
  setSidebarState: (sidebarState: SidebarState) => void;
  isSandbox?: boolean;
}

function Sidebar({
  className,
  userRole,
  inRecordedLessonView: inRecordedLessonView = false,
  lessonId,
  lessonType,
  sidebarState,
  setSidebarState,
  isSandbox = false,
}: SidebarProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.Chat);
  const [activeLunaTab, setActiveLunaTab] = useState<LunaTabName>(
    LunaTabName.Problems
  );

  const isSidebarOpen = sidebarState !== SidebarState.COLLAPSED;

  const handleNavigateToTab = (
    tab: ActiveTab,
    subTab: LunaTabName | undefined
  ) => {
    setSidebarState(SidebarState.STANDARD);
    setActiveTab(tab);
    if (tab === ActiveTab.Luna && subTab) {
      setActiveLunaTab(subTab);
    }
  };

  const isSidebarCollapsed = sidebarState === SidebarState.COLLAPSED;

  return (
    <ConversationProvider>
      <Box
        className={`sidebar-container ${className || ""}`}
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          height: "100%",

          m: 0,
          width: isSidebarOpen ? 400 : 64,
          transition: theme.transitions.create("width", {
            duration: theme.transitions.duration.standard,
            easing: theme.transitions.easing.easeInOut,
          }),
        })}
      >
        <SidebarTabs
          lessonType={lessonType}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onToggleSidebar={() => {
            setSidebarState(
              isSidebarOpen ? SidebarState.COLLAPSED : SidebarState.STANDARD
            );
          }}
          sidebarState={sidebarState}
          setSidebarState={setSidebarState}
          isReadOnly={inRecordedLessonView}
        />
        <Box
          className="sidebar-content"
          sx={{
            height: "100%",
            flexGrow: 1,
            overflowY: "auto",
            overflowX: "hidden",
            width: "100%",
            display: isSidebarCollapsed ? "none" : "block",
          }}
        >
          {inRecordedLessonView ? (
            <ReadOnlyChatPane
              lessonId={lessonId || ""}
              isVisible={activeTab === ActiveTab.Chat}
            />
          ) : (
            <ChatPane
              sidebarState={sidebarState}
              isVisible={activeTab === ActiveTab.Chat}
              isSandbox={isSandbox}
            />
          )}

          <LessonNotes
            readOnly={inRecordedLessonView}
            isVisible={activeTab === ActiveTab.Notes}
          />

          <LunaTutor
            activeTab={activeLunaTab}
            setActiveTab={setActiveLunaTab}
            isVisible={
              activeTab === ActiveTab.Luna && userRole === UserRole.Tutor
            }
          />

          <LunaStudent
            inRecordedLessonView={inRecordedLessonView}
            activeTab={activeLunaTab}
            setActiveTab={setActiveLunaTab}
            isVisible={
              activeTab === ActiveTab.Luna && userRole !== UserRole.Tutor
            }
          />
        </Box>
      </Box>

      {!inRecordedLessonView && (
        <RealtimeSnackbar
          handleNavigateToTab={handleNavigateToTab}
          activeTab={activeTab}
          isSidebarOpen={isSidebarOpen}
        />
      )}
    </ConversationProvider>
  );
}

export default Sidebar;
